<template>
  <div
    class="sv-view-switch"
    :class="{ 'sv-view-switch--phone': phone }"
    role="tablist"
    aria-label="Schedule layout"
    data-test="sv-view-switch"
  >
    <button
      v-for="tab in tabs"
      :key="tab.key"
      type="button"
      role="tab"
      class="sv-view-switch__tab"
      :class="{ 'sv-view-switch__tab--on': view === tab.key }"
      :aria-selected="view === tab.key ? 'true' : 'false'"
      :aria-label="tab.label"
      :title="tab.label"
      :data-test="tab.test"
      @click="pick(tab.key)"
    >
      <i class="fa" :class="tab.icon" aria-hidden="true"></i>
      <span class="sv-view-switch__label">{{ tab.label }}</span>
    </button>
  </div>
</template>

<script>
/**
 * The view switch: Schedule · Table view on an iPad / desktop (Phase 11), Timeline · Columns · Table
 * on a phone (WP-B, 2026-10-23). In the filter row right after the settings gear on a wide screen —
 * where the design puts it (`.view-seg`) — and in a full-width band of its own under the filters on a
 * phone (`.tb-views` / `.view-seg.phone`).
 *
 * PRESENTATIONAL: props in, one event out. Which tabs there are (`tabs`, from tableView.js
 * `viewTabs(isPhone)`), which is on, and remembering it belong to the page and the store
 * (scheduleView/updateView · updatePhoneView). `phone` only picks the full-width three-segment
 * form; it is the page's JS that decides it, not a media query, so the switch and the views it
 * switches between cannot disagree.
 *
 * The WORDS go at tablet width on a wide screen and the buttons keep their icon, `title` and
 * `aria-label` (the design's tablet form), so nothing is left unnamed for a screen reader. A phone's
 * three tabs keep their words — they are short, and the segments are wide.
 */
export default {
  name: 'ScheduleViewSwitch',

  props: {
    /** The key of the tab that is on: 'schedule' | 'table' (wide) or 'timeline' | 'columns' | 'table' (phone). */
    view: { type: String, default: 'schedule' },
    /** [{ key, label, icon, test }] — tableView.js viewTabs(). */
    tabs: { type: Array, required: true },
    /** The phone's form: a full-width pill whose segments share the width equally. */
    phone: { type: Boolean, default: false },
  },

  methods: {
    /** Choosing the view that is already on is not a change — nothing is stored or reloaded for it. */
    pick(key) {
      if (key !== this.view) this.$emit('change', key);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

// The segmented pill of the Forms Settings look: a #eceff0 well, a white segment for the one that is
// on, a teal icon on it. 38px tall to line up with the filter buttons beside it.
.sv-view-switch {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 2px;
  height: 38px;
  box-sizing: border-box;
  padding: 3px;
  border-radius: $sv-radius-pill;
  background: $sv-bg-3;
}

.sv-view-switch__tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border: 0;
  border-radius: 17px;
  background: transparent;
  color: $sv-ink-3;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  .fa { color: $sv-ink-3; font-size: 13px; }

  &:hover:not(.sv-view-switch__tab--on) { color: $sv-ink; }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: 1px;
  }
}

.sv-view-switch__tab--on {
  background: $sv-surface;
  color: $sv-ink;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.1);

  .fa { color: $sv-brand; }
}

// The phone's form: one full-width pill, the three segments sharing it. Its own band is the page's
// (.sv-controls__views); this is only the pill.
.sv-view-switch--phone {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
  height: 42px;

  .sv-view-switch__tab {
    justify-content: center;
    height: 36px;
    padding: 0 8px;
    font-size: 13.5px;
  }
}

// The tablet form: icon only. The label is a span of its own so the words go and the icon, title and
// aria-label stay. Not on the phone's switch — its segments are wide and its words are short.
@media (max-width: $sv-tablet-max) {
  .sv-view-switch:not(.sv-view-switch--phone) .sv-view-switch__tab {
    gap: 0;
    padding: 0 12px;
  }

  .sv-view-switch:not(.sv-view-switch--phone) .sv-view-switch__label { display: none; }
}
</style>
